import { useState, type KeyboardEvent } from 'react';

export interface TagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Max tags allowed (inclusive). 0/undefined = unlimited. */
  max?: number;
  /** Optional regex each tag must match. */
  pattern?: RegExp;
  /** Disabled state. */
  disabled?: boolean;
  /** Accessible label for the underlying input. */
  ariaLabel?: string;
  /** Test id passthrough. */
  testId?: string;
}

/**
 * Input de tags/chips minimal. Enter o coma agrega; Backspace en input vacío
 * borra el último; click en × elimina. Sin dependencias externas.
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  max,
  pattern,
  disabled = false,
  ariaLabel,
  testId,
}: TagInputProps): JSX.Element {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const tryAdd = (raw: string): void => {
    const candidate = raw.trim();
    if (!candidate) return;
    if (value.includes(candidate)) {
      setErr('Duplicado');
      return;
    }
    if (pattern && !pattern.test(candidate)) {
      setErr('Formato inválido');
      return;
    }
    if (max && value.length >= max) {
      setErr(`Máximo ${max}`);
      return;
    }
    setErr(null);
    onChange([...value, candidate]);
    setDraft('');
  };

  const remove = (idx: number): void => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAdd(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value.length - 1);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1">
        {value.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-700"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                aria-label={`remove ${tag}`}
                onClick={() => remove(i)}
                className="text-slate-500 hover:text-red-600"
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={draft}
          disabled={disabled}
          aria-label={ariaLabel}
          placeholder={placeholder}
          onChange={(e) => {
            setDraft(e.target.value);
            if (err) setErr(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (draft.trim()) tryAdd(draft);
          }}
          className="flex-1 min-w-[8ch] border-0 bg-transparent px-1 py-0.5 text-sm outline-none focus:ring-0"
        />
      </div>
      {err && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {err}
        </span>
      )}
    </div>
  );
}
